import { useMemo } from "react";
import { X, Trophy } from "lucide-react";
import { Button } from "./Button";
import { useLanguage } from "../contexts/LanguageContext";
import { useLeaderboard } from "../hooks/useLeaderboard";
import type { Database } from "../lib/database.types";

type LeaderboardRow = Database["public"]["Views"]["leaderboard"]["Row"];

interface PodiumModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartPredicting: () => void;
}

export function PodiumModal({
  isOpen,
  onClose,
  onStartPredicting,
}: PodiumModalProps) {
  const { t } = useLanguage();
  const { leaderboard, loading } = useLeaderboard(3);

  const topThree = useMemo(() => leaderboard.slice(0, 3), [leaderboard]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-primary to-primary-dark p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold">{t("podium.title")}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-smooth"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-white/90 text-sm">{t("podium.subtitle")}</p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Podium Display */}
          <div className="mb-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-slate-400">{t("common.loading")}</div>
              </div>
            ) : (
              <div className="flex items-end justify-center gap-2 mb-6">
                {/* 2nd Place */}
                <div
                  className={`flex-1 text-center max-w-[100px] ${
                    !topThree[1] || topThree[1].total_points === 0
                      ? "opacity-30"
                      : ""
                  }`}
                >
                  <div className="bg-gradient-to-br from-slate-300 to-slate-400 rounded-xl p-4 mb-2 shadow-lg">
                    <div className="text-4xl mb-2">🥈</div>
                    {topThree[1] && topThree[1].total_points > 0 ? (
                      <>
                        <div className="text-xs text-white font-semibold truncate">
                          {topThree[1].name}
                        </div>
                        <div className="text-lg text-white mt-1 font-bold">
                          {topThree[1].total_points}
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-white font-semibold mt-1">
                        -
                      </div>
                    )}
                  </div>
                  <div className="bg-gradient-to-b from-slate-300 to-slate-400 h-16 rounded-t-xl"></div>
                </div>

                {/* 1st Place */}
                <div
                  className={`flex-1 text-center max-w-[120px] ${
                    !topThree[0] || topThree[0].total_points === 0
                      ? "opacity-30"
                      : ""
                  }`}
                >
                  <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-2xl p-5 mb-2 shadow-xl">
                    <div className="text-5xl mb-2">👑</div>
                    {topThree[0] && topThree[0].total_points > 0 ? (
                      <>
                        <div className="text-xs text-primary-dark font-semibold truncate">
                          {topThree[0].name}
                        </div>
                        <div className="text-xl text-primary-dark mt-1 font-bold">
                          {topThree[0].total_points}
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-primary-dark font-semibold mt-1">
                        -
                      </div>
                    )}
                  </div>
                  <div className="bg-gradient-to-b from-yellow-400 to-yellow-500 h-24 rounded-t-2xl"></div>
                </div>

                {/* 3rd Place */}
                <div
                  className={`flex-1 text-center max-w-[100px] ${
                    !topThree[2] || topThree[2].total_points === 0
                      ? "opacity-30"
                      : ""
                  }`}
                >
                  <div className="bg-amber-700 rounded-xl p-4 mb-2 shadow-sm">
                    <div className="text-4xl mb-2">🥉</div>
                    {topThree[2] && topThree[2].total_points > 0 ? (
                      <>
                        <div className="text-xs text-white font-medium truncate">
                          {topThree[2].name}
                        </div>
                        <div className="text-lg text-white mt-1 font-semibold">
                          {topThree[2].total_points}
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-white font-medium mt-1">
                        -
                      </div>
                    )}
                  </div>
                  <div className="bg-amber-700 h-12 rounded-t-xl"></div>
                </div>
              </div>
            )}
          </div>

          {/* Call to Action */}
          <div className="bg-gradient-to-br from-primary-lighter to-primary-light rounded-xl p-4 mb-4 border border-primary/20">
            <div className="flex items-center gap-3 mb-2">
              <Trophy className="w-6 h-6 text-primary" />
              <p className="text-sm font-semibold text-slate-900">
                {t("podium.cta")}
              </p>
            </div>
            <p className="text-xs text-slate-600">{t("podium.description")}</p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button variant="primary" fullWidth onClick={onStartPredicting}>
              {t("podium.startPredicting")}
            </Button>
            <Button variant="secondary" fullWidth onClick={onClose}>
              {t("common.maybeLater")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

